/**
 * Synthesizes a wooden chess piece drop sound using the Web Audio API.
 * No audio files required.
 */
export function playPieceMoveSound(): void {
  try {
    const ctx = new AudioContext();

    // --- Thud body: low-frequency filtered noise burst ---
    const bufferSize = ctx.sampleRate * 0.15;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1);
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    // Band-pass around 180 Hz to mimic wood resonance
    const bandpass = ctx.createBiquadFilter();
    bandpass.type = 'bandpass';
    bandpass.frequency.value = 180;
    bandpass.Q.value = 0.8;

    // Low-pass to remove harshness
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.value = 400;

    // Sharp attack, fast decay envelope
    const gainNode = ctx.createGain();
    const now = ctx.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.9, now + 0.003);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

    noise.connect(bandpass);
    bandpass.connect(lowpass);
    lowpass.connect(gainNode);
    gainNode.connect(ctx.destination);

    noise.start(now);
    noise.stop(now + 0.15);

    // --- Click transient: short sine pop for the initial tap ---
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(900, now);
    osc.frequency.exponentialRampToValueAtTime(120, now + 0.025);

    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.35, now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);

    osc.connect(oscGain);
    oscGain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.03);

    // Clean up context after sound finishes
    setTimeout(() => ctx.close(), 300);
  } catch {
    // AudioContext not available (e.g. SSR / test env) — silently ignore
  }
}

/**
 * Plays a short two-tone chime for incoming lobby invites.
 */
export function playLobbyInviteSound(): void {
  try {
    const ctx = new AudioContext();
    const now = ctx.currentTime;

    const makeTone = (startAt: number, frequency: number, duration: number, gainValue: number) => {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(frequency, startAt);

      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(0.0001, startAt);
      gainNode.gain.exponentialRampToValueAtTime(gainValue, startAt + 0.015);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      osc.start(startAt);
      osc.stop(startAt + duration);
    };

    makeTone(now, 784, 0.2, 0.18);
    makeTone(now + 0.18, 1175, 0.24, 0.2);

    setTimeout(() => ctx.close(), 800);
  } catch {
    // AudioContext not available (e.g. SSR / test env) — silently ignore
  }
}
